// src/pages/EquipmentAdminPage.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Card,
  CardContent,
  Avatar,
  Chip,
  useTheme,
  Alert
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Build as BuildIcon,
  LocationOn as LocationIcon,
  Category as CategoryIcon
} from '@mui/icons-material';

import { getData, saveData } from '../services/storageService';
import { useNotifications } from '../context/NotificationContext';

const EquipmentAdminPage = () => {
  const theme = useTheme();
  const { showNotification } = useNotifications();
  
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState({ 
    id: null, 
    name: '', 
    type: '', 
    model: '', 
    location: '',
    status: 'active'
  });
  const [itemToDelete, setItemToDelete] = useState(null);

  useEffect(() => {
    const loadEquipment = async () => {
      setLoading(true);
      try {
        const data = await getData('equipment') || [];
        setEquipment(data);
      } catch (error) {
        console.error("Error loading equipment:", error);
        showNotification('Error al cargar el equipo', 'error');
      } finally {
        setLoading(false);
      }
    };
    loadEquipment();
  }, [showNotification]);

  const handleOpenDialog = (item = null) => {
    setCurrentItem(item || { 
      id: null, 
      name: '', 
      type: '', 
      model: '', 
      location: '',
      status: 'active'
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => setOpenDialog(false);

  const handleSave = async () => {
    if (!currentItem.name.trim() || !currentItem.type.trim() || !currentItem.model.trim()) {
      showNotification('Por favor, complete todos los campos obligatorios', 'warning');
      return;
    }

    let updatedEquipment = [...equipment];
    
    if (currentItem.id) {
      // Редагування існуючого обладнання
      updatedEquipment = equipment.map(item => 
        item.id === currentItem.id ? currentItem : item
      );
    } else {
      // Додавання нового обладнання
      updatedEquipment.push({ 
        ...currentItem, 
        id: Date.now(),
        createdAt: new Date().toISOString()
      });
    }
    
    try {
      await saveData('equipment', updatedEquipment);
      setEquipment(updatedEquipment);
      handleCloseDialog();
      showNotification(
        currentItem.id ? 'Equipo actualizado correctamente' : 'Equipo agregado correctamente', 
        'success'
      );
    } catch (error) {
      console.error("Error saving equipment:", error);
      showNotification('Error al guardar el equipo', 'error');
    }
  };

  const openDeleteDialog = (item) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    
    try {
      const updatedEquipment = equipment.filter(item => item.id !== itemToDelete.id);
      await saveData('equipment', updatedEquipment);
      setEquipment(updatedEquipment);
      showNotification('Equipo eliminado correctamente', 'success');
    } catch (error) {
      console.error("Error deleting equipment:", error);
      showNotification('Error al eliminar el equipo', 'error');
    } finally {
      closeDeleteDialog();
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentItem(prev => ({ ...prev, [name]: value }));
  };

  const getEquipmentTypeColor = (type) => {
    const typeColors = {
      'lavadora': 'primary',
      'secadora': 'secondary',
      'planchadora': 'success',
      'centrifugadora': 'warning',
      'tunel': 'info',
      'otro': 'default'
    };
    return typeColors[type.toLowerCase()] || 'default';
  };

  const getStatusColor = (status) => {
    return status === 'active' ? 'success' : 'error';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Заголовок */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Avatar
          sx={{
            width: 80,
            height: 80,
            bgcolor: theme.palette.primary.main,
            mx: 'auto',
            mb: 2
          }}
        >
          <BuildIcon sx={{ fontSize: 40 }} />
        </Avatar>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          Gestión de Equipos
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Administrar el inventario de equipos de la lavandería
        </Typography>
      </Box>

      {/* Картка додавання обладнання */}
      <Card sx={{ mb: 4, borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <BuildIcon color="primary" />
              Lista de Equipos ({equipment.length})
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />} 
              onClick={() => handleOpenDialog()}
              sx={{ borderRadius: 2 }}
            >
              Agregar Equipo
            </Button>
          </Box>

          {/* Таблиця обладнання */}
          <TableContainer 
            component={Paper} 
            variant="outlined" 
            sx={{ borderRadius: 2 }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Equipo</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Tipo</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Modelo</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Ubicación</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Estado</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {equipment.map((item) => (
                  <TableRow 
                    key={item.id}
                    sx={{ 
                      '&:hover': { 
                        backgroundColor: theme.palette.action.hover 
                      } 
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ width: 40, height: 40, bgcolor: theme.palette.primary.light }}>
                          <BuildIcon sx={{ color: theme.palette.primary.main }} />
                        </Avatar>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {item.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        icon={<CategoryIcon />}
                        label={item.type}
                        color={getEquipmentTypeColor(item.type)}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {item.model}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                        <Typography variant="body2">
                          {item.location}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={item.status === 'active' ? 'Activo' : 'Inactivo'}
                        color={getStatusColor(item.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton 
                        onClick={() => handleOpenDialog(item)} 
                        color="primary"
                        sx={{ '&:hover': { backgroundColor: theme.palette.primary.light } }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        onClick={() => openDeleteDialog(item)} 
                        color="error"
                        sx={{ '&:hover': { backgroundColor: theme.palette.error.light } }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {equipment.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <BuildIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No hay equipos registrados
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Comience agregando el primer equipo a su inventario
                      </Typography>
                      <Button 
                        variant="contained" 
                        startIcon={<AddIcon />} 
                        onClick={() => handleOpenDialog()}
                        sx={{ borderRadius: 2 }}
                      >
                        Agregar Primer Equipo
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Діалог додавання/редагування обладнання */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {currentItem.id ? 'Editar Equipo' : 'Agregar Nuevo Equipo'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              autoFocus
              name="name"
              label="Nombre del equipo"
              type="text"
              fullWidth
              variant="outlined"
              value={currentItem.name}
              onChange={handleInputChange}
              required
            />
            <TextField
              name="type"
              label="Tipo de equipo"
              type="text"
              fullWidth
              variant="outlined"
              value={currentItem.type}
              onChange={handleInputChange}
              required
              helperText="Ej: Lavadora, Secadora, Planchadora, etc."
            />
            <TextField
              name="model"
              label="Modelo"
              type="text"
              fullWidth
              variant="outlined"
              value={currentItem.model}
              onChange={handleInputChange}
              required
            />
            <TextField
              name="location"
              label="Ubicación"
              type="text"
              fullWidth
              variant="outlined"
              value={currentItem.location}
              onChange={handleInputChange}
              helperText="Área o zona donde se encuentra el equipo"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} sx={{ borderRadius: 2 }}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            startIcon={<SaveIcon />}
            sx={{ borderRadius: 2 }}
          >
            {currentItem.id ? 'Actualizar' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Діалог підтвердження видалення */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle sx={{ fontWeight: 600 }}>
          Confirmar Eliminación
        </DialogTitle>
        <DialogContent>
          <Typography>
            ¿Está seguro de que desea eliminar el equipo <strong>"{itemToDelete?.name}"</strong>?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Esta acción eliminará permanentemente el equipo del inventario.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} sx={{ borderRadius: 2 }}>
            Cancelar
          </Button>
          <Button 
            onClick={handleDelete} 
            variant="contained" 
            color="error" 
            startIcon={<DeleteIcon />}
            sx={{ borderRadius: 2 }}
          >
            Eliminar Equipo
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EquipmentAdminPage;


